import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { toast } from "react-toastify";
import { Button } from "../Buttons";
import { requestAuth } from "../services/RequestService";

export const ConfirmationLDocsForm = ({ data, dialogClose, procedure }) => {
  const [leftDocs, setLeftDocs] = useState(
    data.map((d) => ({ id: d.id, label: d.label, description: d.description }))
  );

  const [rightDocs, setRightDocs] = useState(
    data.map((d, i) => ({
      id: `url-${i}`,
      url: d.description || null,
      originId: d.description ? d.id : null,
    }))
  );

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    if (source.droppableId === "right" && destination.droppableId === "right") {
      const newRight = Array.from(rightDocs);
      const [removed] = newRight.splice(source.index, 1);
      newRight.splice(destination.index, 0, removed);
      setRightDocs(newRight);
    }

    if (source.droppableId === "right" && destination.droppableId === "left") {
      const dragged = rightDocs[source.index];
      if (!dragged || !dragged.url) return;

      const newLeftDocs = leftDocs.map((doc) => {
        if (doc.id === dragged.originId) {
          return { ...doc, description: null };
        }
        if (doc.id === leftDocs[destination.index].id) {
          return { ...doc, description: dragged.url };
        }
        return doc;
      });

      const newRightDocs = rightDocs.map((r, i) =>
        i === source.index ? { ...r, url: null, originId: null } : r
      );

      setLeftDocs(newLeftDocs);
      setRightDocs(newRightDocs);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const updatedDocs = leftDocs.map((doc, index) => ({
      ...doc,
      description: rightDocs[index]?.url || null,
    }));

    try {
      await requestAuth(
        "put",
        `/dataSet/subData/confirmationLDocs`,
        updatedDocs
      );
      toast.success("Guardado correctamente.");
      dialogClose();
    } catch (error) {
      toast.error(
        "Se produjo un error al confirmar el orden de los documentos."
      );
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 p-4 mb-5 bg-gray-50 rounded-lg overflow-hidden justify-center w-full"
      >
        <div>
          <h2 className="font-semibold text-center uppercase">
            Confirmación de documentos
          </h2>
        </div>
        <div>
          <span className="text-sm">
            Es posible que haya un desfase en el trámite{" "}
            <span className="font-bold text-red-700">{procedure?.numRef}</span>{" "}
            al extraer los documentos. <br />
            Por favor, confirme el orden de los enlaces según la sección L:
          </span>
        </div>
        <div className="flex w-full gap-2 overflow-auto">
          <Droppable droppableId="left">
            {(provided, snapshot) => (
              <table
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`border-collapse border border-gray-300 rounded-lg shadow-sm bg-white transition-all ${
                  snapshot.isDraggingOver
                    ? "ring-2 ring-blue-400 bg-blue-50"
                    : ""
                }`}
              >
                <thead>
                  <tr>
                    <th className="border border-gray-300 bg-[#1c3144] text-white px-4 py-2 font-semibold">
                      Nombre documento
                    </th>
                    <th className="border border-gray-300 bg-[#1c3144] text-white px-4 py-2 font-semibold">
                      URL
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leftDocs.map((doc, index) => (
                    <Draggable
                      key={doc.id.toString()}
                      draggableId={doc.id.toString()}
                      index={index}
                      isDragDisabled
                    >
                      {(provided) => (
                        <tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="border border-gray-300 px-4 py-2 font-medium text-gray-800 max-w-[300px] truncate">
                            {doc.label}
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            {doc.description ? (
                              <a
                                href={doc.description}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                              >
                                🔗 Ver
                              </a>
                            ) : (
                              <span className="text-gray-400 italic">
                                (vacio)
                              </span>
                            )}
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
              </table>
            )}
          </Droppable>

          <Droppable droppableId="right">
            {(provided, snapshot) => (
              <table
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`border-collapse border border-gray-300 rounded-lg shadow-sm bg-white transition-all ${
                  snapshot.isDraggingOver
                    ? "ring-2 ring-[#ffe99a] bg-[#fffbeb]"
                    : ""
                }`}
              >
                <thead>
                  <tr>
                    <th className="border border-gray-300 bg-[#FBCE00] px-4 py-2 font-semibold">
                      URLs disponibles
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rightDocs.map((doc, index) => (
                    <Draggable key={doc.id} draggableId={doc.id} index={index}>
                      {(provided, snapshot) => (
                        <tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`${
                            snapshot.isDragging
                              ? "bg-blue-100 shadow-md"
                              : "hover:bg-gray-50"
                          } ${!doc.url ? "opacity-60" : "cursor-grab"}`}
                        >
                          <td className="border border-gray-300 px-2 py-2 max-w-[180px]">
                            {doc.url ? (
                              <a
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#745a00] hover:text-[#d4a600] font-medium underline transition-colors truncate block"
                              >
                                🔗 Enlace de documento
                              </a>
                            ) : (
                              <span className="text-gray-400 italic">
                                Sin URL
                              </span>
                            )}
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
              </table>
            )}
          </Droppable>
        </div>
        <div className="flex justify-end pt-2">
          <Button type="submit" className="w-full">
            Guardar cambios
          </Button>
        </div>
      </form>
    </DragDropContext>
  );
};
